import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import {
	BreadcrumbWrapper,
	BreadcrumbContainer,
	SolutionCenterC2Text,
	HiddenBreadcrumbButton,
	SolutionCenterUl,
	BreadcrumbPopoverItem,
} from './elements';
import Popover from '../general-components/popover';
import { ZendeskCategory, ZendeskSection, SolutionCenterViews } from '../../types/solution-center';
import { GreenroomState as GS } from '../../stores/types/types';

interface BreadcrumbsProps {
	loadingContent: boolean;
	currentCategory: ZendeskCategory | undefined;
	currentSection: ZendeskSection | undefined;
	handleSetHomeView: () => void;
	handleSelectCategory: (categoryId?: number) => void;
	handleSelectSection: (sectionId?: number) => void;
}

export default function Breadcrumbs(props: BreadcrumbsProps) {
	const {
		loadingContent,
		currentCategory,
		currentSection,
		handleSetHomeView,
		handleSelectCategory,
		handleSelectSection,
	} = props;

	const view = useSelector((s: GS) => s.UserInterfaceReducer.solutionCenterView);

	const [breadcrumbRefWidth, setBreadcrumbRefWidth] = useState<number>(0);
	const [hiddenBreadcrumbCount, setHiddenBreadcrumbCount] = useState<number>(0);
	const [breadcrumbPopoverOpen, setBreadcrumbPopoverOpen] = useState<boolean>(false);

	const breadcrumbRef = useRef<HTMLDivElement>(null);

	const getHiddenBreadcrumbs = () => {
		if (breadcrumbRef.current) {
			// reset the hidden breadcrumb count
			let hiddenBreadcrumbLength = 0;
			setHiddenBreadcrumbCount(0);

			// determine if the breadcrumb container is overflowing and by how much
			let excessWidth = breadcrumbRef.current.scrollWidth - breadcrumbRef.current.clientWidth;
			if (excessWidth > 0) excessWidth += 16; // account for ellipsis button

			breadcrumbRef.current.childNodes.forEach((child) => {
				if (!breadcrumbRef.current || !(child instanceof HTMLElement) || excessWidth <= 0) return;

				// to determine if still overflowing after removing the child
				excessWidth -= child.clientWidth;

				// the last child will still display the element with text-overflow set to ellipsis
				if (child !== breadcrumbRef.current.lastChild) {
					hiddenBreadcrumbLength += 1;
				}
			});

			setHiddenBreadcrumbCount(hiddenBreadcrumbLength);
		}
	};

	const resizeObserver = new ResizeObserver((entries) => {
		if (entries[0].contentRect.width && entries[0].contentRect.width !== breadcrumbRefWidth) {
			setBreadcrumbRefWidth(entries[0].contentRect.width);
			getHiddenBreadcrumbs();
		}
	});

	useEffect(() => {
		if (breadcrumbRef.current) {
			resizeObserver.observe(breadcrumbRef.current);
		}

		return () => {
			if (breadcrumbRef.current) {
				resizeObserver.disconnect();
			}
		};
	}, [breadcrumbRef, resizeObserver]);

	const breadcrumbs = useMemo(() => [
		{
			name: 'Solution Center',
			onClick: () => handleSetHomeView(),
			associatedView: SolutionCenterViews.Home,
		},
		...(currentCategory ? [{
			name: currentCategory?.name,
			onClick: () => handleSelectCategory(),
			associatedView: SolutionCenterViews.Category,
		}] : []),
		...(currentSection ? [{
			name: currentSection?.name,
			onClick: () => handleSelectSection(),
			associatedView: SolutionCenterViews.Section,
		}] : []),
	], [currentCategory, currentSection]);

	return (
		<>
			<BreadcrumbWrapper
				ref={breadcrumbRef}
				className={classNames(loadingContent && 'loading-content')}
			>
				{hiddenBreadcrumbCount > 0 && (
					<HiddenBreadcrumbButton
						onClick={() => setBreadcrumbPopoverOpen(!breadcrumbPopoverOpen)}
					>
						<Icon
							name={ICONS.THREE_DOTS_HORIZONTAL}
							size={10}
							color={COLORS.TEXT_GRAY_100}
						/>
					</HiddenBreadcrumbButton>
				)}
				{breadcrumbs.map((b, i) => (
					<BreadcrumbContainer
						key={`breadcrumb-${i}`}
						style={{ display: hiddenBreadcrumbCount > i ? 'none' : 'flex' }}
						className={classNames(
							hiddenBreadcrumbCount === i
							&& view === SolutionCenterViews.Home
							&& 'hide-overflow',
						)}
					>
						{i !== 0 && (
							<SolutionCenterC2Text className="caret">&nbsp;&gt;&nbsp;</SolutionCenterC2Text>
						)}
						<SolutionCenterC2Text
							className={classNames(view !== b.associatedView && 'can-click')}
							onClick={() => b.onClick()}
						>
							{b.name}
						</SolutionCenterC2Text>
					</BreadcrumbContainer>
				))}
			</BreadcrumbWrapper>
			<Popover
				open={breadcrumbPopoverOpen}
				close={() => setBreadcrumbPopoverOpen(false)}
				className="hidden-breadcrumbs"
			>
				<SolutionCenterUl className="breadcrumb-popover">
					{Array.from({ length: hiddenBreadcrumbCount }, (_, i) => (
						<BreadcrumbPopoverItem
							key={`hidden-breadcrumb-${i}`}
							onClick={() => {
								breadcrumbs[i].onClick();
								setBreadcrumbPopoverOpen(false);
							}}
						>
							<SolutionCenterC2Text className="breadcrumb-popover">
								{breadcrumbs[i].name}
							</SolutionCenterC2Text>
						</BreadcrumbPopoverItem>
					))}
				</SolutionCenterUl>
			</Popover>
		</>
	);
}
